import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';

const EmailVerificationBanner = () => {
  const { user, token, refreshUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show if user is verified or banner is dismissed
  if (!user || user.email_verified || dismissed) {
    return null;
  }

  const handleSendVerification = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/auth/send-verification`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Verification email sent! Check your inbox.');
        
        // For demo purposes, show the token (remove in production)
        if (data.verification_token) {
          toast.info(`Demo: Use token "${data.verification_token}" to verify`, {
            duration: 10000
          });
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send verification email');
      }
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="relative z-40 px-4 pt-16 sm:pt-20 -mb-16"
      >
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-[#7000FF]/20 to-[#00F0FF]/20 backdrop-blur-lg border border-[#7000FF]/30 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#7000FF]" />
              </div>
              <div>
                <p className="font-medium text-sm sm:text-base">Verify your email to unlock all features</p>
                <p className="text-white/60 text-xs sm:text-sm">
                  Send tips to creators and access premium features
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                onClick={handleSendVerification}
                disabled={sending}
                className="btn-primary text-sm px-4 py-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Verification
                  </>
                )}
              </Button>
              <button
                onClick={() => setDismissed(true)}
                className="p-2 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EmailVerificationBanner;
